import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth, storage } from '../config/firebase';

class FirebaseChatService {
  constructor() {
    this.conversationsRef = collection(db, 'conversations');
    this.messagesRef = collection(db, 'messages');
    this.usersRef = collection(db, 'users');
  }

  // Convert Firestore Timestamp/Date/number to ms number (serializable)
  toMillis(value) {
    if (!value) return 0;
    // Firestore Timestamp
    if (value && typeof value.toDate === 'function') {
      try {
        return value.toDate().getTime();
      } catch (_) {}
    }
    // Date instance
    if (value instanceof Date) return value.getTime();
    // already number or ISO string
    const date = new Date(value);
    const ms = date.getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }

  // Get current user
  getCurrentUser() {
    return auth.currentUser;
  }

  // Resolve user by email -> returns userId or null
  async getUserByEmail(email) {
    try {
      if (!email) return null;
      const q = query(this.usersRef, where('email', '==', email), limit(1));
      const snap = await getDocs(q);
      if (snap && !snap.empty) {
        const docSnap = snap.docs[0];
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (e) {
      console.error('Error getting user by email:', e);
      return null;
    }
  }

  // Find existing conversation with specific participant
  async findConversationWithUser(targetUserId) {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !targetUserId) return null;
    const conversations = await this.getConversations();
    return (
      conversations.find(
        c =>
          Array.isArray(c.participants) &&
          c.participants.includes(targetUserId),
      ) || null
    );
  }

  // Ensure user document exists
  async ensureUserDocument() {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) return;

      const userDoc = doc(this.usersRef, currentUser.uid);
      const userDocSnapshot = await getDoc(userDoc);

      if (!userDocSnapshot.exists()) {
        await updateDoc(userDoc, {
          displayName: currentUser.displayName || currentUser.email,
          email: currentUser.email,
          photoURL: currentUser.photoURL || 'https://via.placeholder.com/50',
          isOnline: false,
          lastSeen: serverTimestamp(),
          createdAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error('Error ensuring user document:', error);
    }
  }

  // Create a new conversation
  async createConversation(participantIds, conversationName = null) {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) throw new Error('User not authenticated');

      const participants = [currentUser.uid, ...participantIds];
      const targetParticipantId = participantIds && participantIds[0];

      // 1) Reuse existing 1-1 conversation if it already exists
      try {
        const q = query(
          this.conversationsRef,
          where('participants', 'array-contains', currentUser.uid),
        );
        const snapshot = await getDocs(q);

        if (snapshot && !snapshot.empty) {
          for (const docSnap of snapshot.docs) {
            const data = docSnap.data();
            const hasSameTwo =
              Array.isArray(data.participants) &&
              data.participants.length === 2 &&
              data.participants.includes(currentUser.uid) &&
              data.participants.includes(targetParticipantId);
            if (hasSameTwo) {
              const updates = { isActive: true };
              if (
                Array.isArray(data.deletedBy) &&
                data.deletedBy.includes(currentUser.uid)
              ) {
                updates.deletedBy = arrayRemove(currentUser.uid);
              }
              const unreadCount = data.unreadCount || {};
              if (unreadCount[currentUser.uid] === undefined)
                unreadCount[currentUser.uid] = 0;
              if (unreadCount[targetParticipantId] === undefined)
                unreadCount[targetParticipantId] = 0;

              await updateDoc(doc(this.conversationsRef, docSnap.id), {
                ...updates,
                unreadCount,
              });

              return {
                id: docSnap.id,
                ...data,
                unreadCount,
                isActive: true,
                createdAt: this.toMillis(data.createdAt) || Date.now(),
                lastMessageTime: this.toMillis(data.lastMessageTime),
                clearedAtBy: (() => {
                  const raw = data.clearedAtBy || {};
                  const mapped = {};
                  try {
                    Object.keys(raw).forEach(uid => {
                      mapped[uid] = this.toMillis(raw[uid]);
                    });
                  } catch (_) {}
                  return mapped;
                })(),
              };
            }
          }
        }
      } catch (_) {
        // If search fails, we will create a new conversation below
      }

      const conversationData = {
        participants,
        conversationName:
          conversationName || `Chat with ${participantIds.length} people`,
        createdAt: serverTimestamp(),
        lastMessage: null,
        lastMessageTime: null,
        unreadCount: {},
        isActive: true,
      };

      // Initialize unread count for all participants
      participants.forEach(participantId => {
        conversationData.unreadCount[participantId] = 0;
      });

      const docRef = await addDoc(this.conversationsRef, conversationData);

      return {
        id: docRef.id,
        ...conversationData,
        createdAt: Date.now(),
      };
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }

  // Get conversations for current user
  async getConversations() {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) throw new Error('User not authenticated');

      const q = query(
        this.conversationsRef,
        where('participants', 'array-contains', currentUser.uid),
      );
      const snapshot = await getDocs(q);

      if (!snapshot || snapshot.empty) {
        return [];
      }

      const conversations = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();

        // Check if current user has deleted this conversation
        if (data.deletedBy && data.deletedBy.includes(currentUser.uid)) {
          continue;
        }

        // Only show conversations that have messages
        if (!data.lastMessage || data.lastMessage === null) {
          continue;
        }

        const clearedAtByRaw = data.clearedAtBy || {};
        const clearedAtBy = {};
        try {
          Object.keys(clearedAtByRaw).forEach(uid => {
            clearedAtBy[uid] = this.toMillis(clearedAtByRaw[uid]);
          });
        } catch (_) {}

        const conversation = {
          id: docSnap.id,
          ...data,
          lastMessageTime: this.toMillis(data.lastMessageTime),
          createdAt: this.toMillis(data.createdAt),
          clearedAtBy,
        };

        // Get participant details
        const otherParticipants = data.participants.filter(
          id => id !== currentUser.uid,
        );
        if (otherParticipants.length > 0) {
          try {
            const participantDoc = await getDoc(
              doc(this.usersRef, otherParticipants[0]),
            );
            if (participantDoc.exists()) {
              const participantData = participantDoc.data();
              conversation.participantName =
                participantData.displayName || participantData.email;
              conversation.participantAvatar = participantData.photoURL || null;
              conversation.participantId = otherParticipants[0];
            }
          } catch (participantError) {
            console.log('Error getting participant data:', participantError);
            conversation.participantName = 'Unknown User';
            conversation.participantAvatar = 'https://via.placeholder.com/50';
            conversation.participantId = otherParticipants[0];
          }
        }

        conversations.push(conversation);
      }

      // Sort conversations by lastMessageTime on client side
      conversations.sort((a, b) => {
        const timeA = a.lastMessageTime || 0;
        const timeB = b.lastMessageTime || 0;
        return timeB - timeA; // Descending order (newest first)
      });

      return conversations;
    } catch (error) {
      console.error('Error getting conversations:', error);
      return [];
    }
  }

  // Listen to conversations in real-time
  subscribeToConversations(callback) {
    const currentUser = this.getCurrentUser();
    if (!currentUser) return () => {};

    const q = query(
      this.conversationsRef,
      where('participants', 'array-contains', currentUser.uid),
    );

    return onSnapshot(
      q,
      async snapshot => {
        if (!snapshot || snapshot.empty) {
          callback([]);
          return;
        }

        const conversations = [];

        for (const docSnap of snapshot.docs) {
          const data = docSnap.data();

          if (data.deletedBy && data.deletedBy.includes(currentUser.uid)) {
            continue;
          }

          if (!data.lastMessage || data.lastMessage === null) {
            continue;
          }

          const clearedAtByRaw = data.clearedAtBy || {};
          const clearedAtBy = {};
          try {
            Object.keys(clearedAtByRaw).forEach(uid => {
              clearedAtBy[uid] = this.toMillis(clearedAtByRaw[uid]);
            });
          } catch (_) {}

          const conversation = {
            id: docSnap.id,
            ...data,
            lastMessageTime: this.toMillis(data.lastMessageTime),
            createdAt: this.toMillis(data.createdAt),
            clearedAtBy,
          };

          const otherParticipants = data.participants.filter(
            id => id !== currentUser.uid,
          );
          if (otherParticipants.length > 0) {
            try {
              const participantDoc = await getDoc(
                doc(this.usersRef, otherParticipants[0]),
              );
              if (participantDoc.exists()) {
                const participantData = participantDoc.data();
                conversation.participantName =
                  participantData.displayName || participantData.email;
                conversation.participantId = otherParticipants[0];
              }
            } catch (participantError) {
              console.log(
                'Error getting participant data in real-time:',
                participantError,
              );
              conversation.participantName = 'Unknown User';
              conversation.participantId = otherParticipants[0];
            }
          }

          conversations.push(conversation);
        }

        conversations.sort((a, b) => {
          const timeA = a.lastMessageTime || 0;
          const timeB = b.lastMessageTime || 0;
          return timeB - timeA;
        });

        callback(conversations);
      },
      error => {
        console.error('Error in real-time conversations subscription:', error);
        callback([]);
      },
    );
  }

  // Get messages for a conversation
  async getMessages(conversationId, messageLimit = 50) {
    try {
      let clearedAtMs = 0;
      try {
        const currentUser = this.getCurrentUser();
        if (currentUser) {
          const convDoc = await getDoc(
            doc(this.conversationsRef, conversationId),
          );
          const data = convDoc.exists() ? convDoc.data() : null;
          const clearedAtBy = data?.clearedAtBy || {};
          const clearedValue = clearedAtBy[currentUser.uid];
          if (clearedValue) {
            clearedAtMs = this.toMillis(clearedValue);
          }
        }
      } catch (_) {}

      // Don't use orderBy to avoid index requirement - we'll sort client-side
      // Use limit to reduce Firestore read costs (default 50, max 200 for safety)
      const effectiveLimit = Math.min(messageLimit || 50, 200);
      const q = query(
        this.messagesRef,
        where('conversationId', '==', conversationId),
        limit(effectiveLimit),
      );
      const snapshot = await getDocs(q);

      if (!snapshot || snapshot.empty) {
        return [];
      }

      const list = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: this.toMillis(docSnap.data().createdAt),
      }));

      const visible = list.filter(m => {
        const ts = m.createdAt || 0;
        if (ts > (clearedAtMs || 0)) return true;
        if (!ts && m.senderId === (this.getCurrentUser()?.uid || ''))
          return true;
        return false;
      });

      visible.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      return visible;
    } catch (error) {
      console.error('Error getting messages:', error);
      return [];
    }
  }

  // Listen to messages in real-time
  subscribeToMessages(conversationId, callback) {
    let unsubscribe = () => {};
    const currentUser = this.getCurrentUser();

    const attach = clearedAtMs => {
      // Don't use orderBy to avoid index requirement - we'll sort client-side
      const q = query(
        this.messagesRef,
        where('conversationId', '==', conversationId),
      );
      unsubscribe = onSnapshot(
        q,
        snapshot => {
          if (!snapshot || snapshot.empty) {
            callback([]);
            return;
          }

          const messages = snapshot.docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data(),
            createdAt: this.toMillis(docSnap.data().createdAt),
          }));

          const currentUserId = this.getCurrentUser()?.uid || '';
          const visible = messages.filter(m => {
            const ts = m.createdAt || 0;
            if (ts > (clearedAtMs || 0)) return true;
            if (!ts && m.senderId === currentUserId) return true;
            return false;
          });

          visible.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
          callback(visible);
        },
        error => {
          console.error('Error in real-time messages subscription:', error);
          callback([]);
        },
      );
    };

    if (currentUser) {
      getDoc(doc(this.conversationsRef, conversationId))
        .then(docSnap => {
          const data = docSnap.exists() ? docSnap.data() : null;
          const clearedAtBy = data?.clearedAtBy || {};
          const clearedValue = clearedAtBy[currentUser.uid];
          const clearedAtMs = clearedValue ? this.toMillis(clearedValue) : 0;
          attach(clearedAtMs);
        })
        .catch(() => attach(0));
    } else {
      attach(0);
    }

    return () => unsubscribe();
  }

  // Send a message
  async sendMessage(
    conversationId,
    text,
    replyToText,
    replyToType,
    replyToImageUrl,
    replyToFileName,
  ) {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) throw new Error('User not authenticated');

      const conversationDoc = await getDoc(
        doc(this.conversationsRef, conversationId),
      );
      if (!conversationDoc.exists()) {
        throw new Error('Conversation not found');
      }

      const conversationData = conversationDoc.data();
      const otherParticipants = conversationData.participants.filter(
        id => id !== currentUser.uid,
      );

      // Check if any of the other participants have blocked the current user
      for (const participantId of otherParticipants) {
        const isBlocked = await this.isUserBlocked(participantId);
        if (isBlocked) {
          throw new Error('BLOCKED_BY_USER');
        }
      }

      // Check if current user has blocked any of the other participants
      const currentUserDoc = await getDoc(doc(this.usersRef, currentUser.uid));
      if (currentUserDoc.exists()) {
        const currentUserData = currentUserDoc.data();
        const blockedUsers = currentUserData.blockedUsers || [];
        for (const participantId of otherParticipants) {
          if (blockedUsers.includes(participantId)) {
            throw new Error('BLOCKED_BY_ME');
          }
        }
      }

      const messageData = {
        conversationId,
        text: text.trim(),
        senderId: currentUser.uid,
        senderName: currentUser.displayName || currentUser.email,
        createdAt: serverTimestamp(),
        isRead: false,
      };
      if (replyToText) messageData.replyToText = replyToText;
      if (replyToType) messageData.replyToType = replyToType;
      if (replyToImageUrl) messageData.replyToImageUrl = replyToImageUrl;
      if (replyToFileName) messageData.replyToFileName = replyToFileName;

      const messageRef = await addDoc(this.messagesRef, messageData);

      await updateDoc(doc(this.conversationsRef, conversationId), {
        lastMessage: text.trim(),
        lastMessageTime: serverTimestamp(),
        [`unreadCount.${currentUser.uid}`]: 0,
      });

      const updates = {};
      otherParticipants.forEach(participantId => {
        updates[`unreadCount.${participantId}`] = increment(1);
      });

      if (Object.keys(updates).length > 0) {
        await updateDoc(doc(this.conversationsRef, conversationId), updates);
      }

      return {
        id: messageRef.id,
        ...messageData,
        createdAt: Date.now(),
      };
    } catch (error) {
      throw error;
    }
  }

  // Upload a file to Firebase Storage
  async uploadFileForConversation(
    conversationId,
    file,
    fileName = 'file.jpg',
    contentType = 'image/jpeg',
  ) {
    const currentUser = this.getCurrentUser();
    if (!currentUser) throw new Error('User not authenticated');
    const safeName = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
    const objectPath = `messages/${conversationId}/${Date.now()}_${currentUser.uid}_${safeName}`;
    const storageRef = ref(storage, objectPath);

    try {
      await uploadBytes(storageRef, file, {
        contentType: contentType || 'image/jpeg',
      });
    } catch (e) {
      console.error('Storage upload error:', e);
      throw e;
    }

    // Get download URL
    let url;
    try {
      url = await getDownloadURL(storageRef);
    } catch (err) {
      console.error('getDownloadURL failed after upload:', err);
      throw err;
    }

    return { url, objectPath, contentType };
  }

  // Send image message
  async sendImageMessage(conversationId, image) {
    const currentUser = this.getCurrentUser();
    if (!currentUser) throw new Error('User not authenticated');

    const conversationDoc = await getDoc(
      doc(this.conversationsRef, conversationId),
    );
    if (!conversationDoc.exists()) throw new Error('Conversation not found');
    const conversationData = conversationDoc.data();
    const otherParticipants = (conversationData.participants || []).filter(
      id => id !== currentUser.uid,
    );

    let contentType = image.type || 'image/jpeg';
    if (/heic|heif/i.test(contentType || '')) contentType = 'image/jpeg';
    let fileName = image.fileName || 'photo.jpg';
    if (/heic|heif/i.test(fileName))
      fileName = fileName.replace(/heic|heif/i, 'jpg');

    const uploaded = await this.uploadFileForConversation(
      conversationId,
      image.file,
      fileName,
      contentType,
    );

    const messageData = {
      conversationId,
      senderId: currentUser.uid,
      senderName: currentUser.displayName || currentUser.email,
      createdAt: serverTimestamp(),
      isRead: false,
      type: 'image',
      imageUrl: uploaded.url,
      imageWidth: image.width || null,
      imageHeight: image.height || null,
      fileName,
      mimeType: contentType,
      fileSize: image.fileSize || image.size || null,
    };

    const messageRef = await addDoc(this.messagesRef, messageData);

    await updateDoc(doc(this.conversationsRef, conversationId), {
      lastMessage: '[Photo]',
      lastMessageTime: serverTimestamp(),
      [`unreadCount.${currentUser.uid}`]: 0,
    });

    const updates = {};
    otherParticipants.forEach(participantId => {
      updates[`unreadCount.${participantId}`] = increment(1);
    });
    if (Object.keys(updates).length > 0) {
      await updateDoc(doc(this.conversationsRef, conversationId), updates);
    }

    return {
      id: messageRef.id,
      ...messageData,
      createdAt: Date.now(),
    };
  }

  // Send file message
  async sendFileMessage(conversationId, file) {
    const currentUser = this.getCurrentUser();
    if (!currentUser) throw new Error('User not authenticated');

    const conversationDoc = await getDoc(
      doc(this.conversationsRef, conversationId),
    );
    if (!conversationDoc.exists()) throw new Error('Conversation not found');
    const conversationData = conversationDoc.data();
    const otherParticipants = (conversationData.participants || []).filter(
      id => id !== currentUser.uid,
    );

    let contentType = file.type || 'application/octet-stream';
    let fileName = file.fileName || 'file';

    const uploaded = await this.uploadFileForConversation(
      conversationId,
      file.file,
      fileName,
      contentType,
    );

    const messageData = {
      conversationId,
      senderId: currentUser.uid,
      senderName: currentUser.displayName || currentUser.email,
      createdAt: serverTimestamp(),
      isRead: false,
      type: 'file',
      fileUrl: uploaded.url,
      fileName,
      mimeType: contentType,
      fileSize: file.size || null,
    };

    const messageRef = await addDoc(this.messagesRef, messageData);

    await updateDoc(doc(this.conversationsRef, conversationId), {
      lastMessage: `[File] ${fileName}`,
      lastMessageTime: serverTimestamp(),
      [`unreadCount.${currentUser.uid}`]: 0,
    });

    const updates = {};
    otherParticipants.forEach(participantId => {
      updates[`unreadCount.${participantId}`] = increment(1);
    });
    if (Object.keys(updates).length > 0) {
      await updateDoc(doc(this.conversationsRef, conversationId), updates);
    }

    return {
      id: messageRef.id,
      ...messageData,
      createdAt: Date.now(),
    };
  }

  // Mark conversation as read
  async markConversationAsRead(conversationId) {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) return;
      if (!conversationId || String(conversationId).startsWith('temp_'))
        return;

      const docRef = doc(this.conversationsRef, conversationId);
      const snapshot = await getDoc(docRef);
      if (!snapshot.exists()) {
        return;
      }

      await updateDoc(docRef, {
        [`unreadCount.${currentUser.uid}`]: 0,
      });
    } catch (error) {
      if (error?.code === 'firestore/not-found') {
        return;
      }
      console.error('Error marking conversation as read:', error);
    }
  }

  // Update user's online status
  async updateUserOnlineStatus(isOnline) {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) return;

      const userDoc = doc(this.usersRef, currentUser.uid);
      await updateDoc(userDoc, {
        displayName: currentUser.displayName || currentUser.email,
        email: currentUser.email,
        photoURL: currentUser.photoURL || 'https://via.placeholder.com/50',
        isOnline,
        lastSeen: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating online status:', error);
    }
  }

  // Listen to user's online status
  subscribeToUserStatus(userId, callback) {
    return onSnapshot(
      doc(this.usersRef, userId),
      docSnap => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const status = {
            isOnline: !!data.isOnline,
            lastSeen: this.toMillis(data.lastSeen),
          };
          if (typeof callback === 'function') callback(status);
        } else {
          const status = { isOnline: false, lastSeen: 0 };
          if (typeof callback === 'function') callback(status);
        }
      },
      error => {
        console.error('Error subscribing to user status:', error);
        const status = { isOnline: false, lastSeen: 0 };
        if (typeof callback === 'function') callback(status);
      },
    );
  }

  // Search conversations
  async searchConversations(queryText) {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) throw new Error('User not authenticated');

      const q = query(
        this.conversationsRef,
        where('participants', 'array-contains', currentUser.uid),
        where('isActive', '==', true),
      );
      const snapshot = await getDocs(q);

      const conversations = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const conversation = {
          id: docSnap.id,
          ...data,
          lastMessageTime: this.toMillis(data.lastMessageTime),
        };

        const otherParticipants = data.participants.filter(
          id => id !== currentUser.uid,
        );
        if (otherParticipants.length > 0) {
          const participantDoc = await getDoc(
            doc(this.usersRef, otherParticipants[0]),
          );
          if (participantDoc.exists()) {
            const participantData = participantDoc.data();
            conversation.participantName =
              participantData.displayName || participantData.email;
            conversation.participantAvatar =
              participantData.photoURL || 'https://via.placeholder.com/50';

            if (
              conversation.participantName
                .toLowerCase()
                .includes(queryText.toLowerCase()) ||
              conversation.lastMessage
                ?.toLowerCase()
                .includes(queryText.toLowerCase())
            ) {
              conversations.push(conversation);
            }
          }
        }
      }

      return conversations;
    } catch (error) {
      console.error('Error searching conversations:', error);
      throw error;
    }
  }

  // Delete conversation
  async deleteConversation(conversationId) {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) throw new Error('User not authenticated');

      await updateDoc(doc(this.conversationsRef, conversationId), {
        deletedBy: arrayUnion(currentUser.uid),
        clearedAtBy: {
          [currentUser.uid]: serverTimestamp(),
        },
      });
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  }

  // Block user
  async blockUser(blockedUserId) {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) throw new Error('User not authenticated');

      const userDoc = doc(this.usersRef, currentUser.uid);
      await updateDoc(userDoc, {
        blockedUsers: arrayUnion(blockedUserId),
      });
    } catch (error) {
      console.error('Error blocking user:', error);
      throw error;
    }
  }

  // Unblock user
  async unblockUser(blockedUserId) {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) throw new Error('User not authenticated');

      const userDoc = doc(this.usersRef, currentUser.uid);
      await updateDoc(userDoc, {
        blockedUsers: arrayRemove(blockedUserId),
      });
    } catch (error) {
      console.error('Error unblocking user:', error);
      throw error;
    }
  }

  // Check if current user is blocked by the specified user
  async isUserBlocked(userId) {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) return false;

      const userDoc = await getDoc(doc(this.usersRef, userId));
      if (!userDoc.exists()) return false;

      const userData = userDoc.data();
      return userData.blockedUsers?.includes(currentUser.uid) || false;
    } catch (error) {
      console.error('Error checking if user is blocked:', error);
      return false;
    }
  }

  // Report user
  async reportUser(reportedUserId, reason = 'Inappropriate behavior') {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) throw new Error('User not authenticated');

      await updateDoc(doc(this.usersRef, reportedUserId), {
        reports: arrayUnion({
          reportedBy: currentUser.uid,
          reason: reason,
          timestamp: serverTimestamp(),
        }),
      });
    } catch (error) {
      console.error('Error reporting user:', error);
      throw error;
    }
  }

  // Delete message
  async deleteMessage(messageId) {
    try {
      await deleteDoc(doc(this.messagesRef, messageId));
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  }
}

// Create singleton instance
const chatService = new FirebaseChatService();

export default chatService;

